<script>
    import Vue from "vue";
    import {timeFormat} from "d3-time-format";
    import {timeParse} from "d3-time-format";

    import utils from "@/utils.js";

    let copy = obj => JSON.parse(JSON.stringify(obj));

    export default {
        name: "NoteEdit",
        props: {
            note: Object,
            type: String
        },
        data() {
            let typeMapping = {
                people: "Name",
                places: "Place name",
                notes: "Title"
            };

            return {
                // want a copy of creds, and not a props object
                noteEdit: copy(this.note),
                typeLabel: typeMapping[this.type],
                unsubscribeActions: null,
                showing: "edit",
            };
        },
        methods: {
            dismissEdit() {
                this.$emit("save");
            },
            saveChanges(e) {
                let note = copy(this.noteEdit);
                note.type = this.type;
                this.$emit("save", note);
            },
            deleteNote() {
                this.$emit("delete", copy(this.noteEdit));
            },
            toggle(viewName) {
                this.showing = viewName;
            },
            formatTs(ts) {
                ts = timeParse("%Y-%m-%d %H:%M:%S")(ts);
                let now = new Date();
                if (now.getFullYear() == ts.getFullYear()) {
                    return timeFormat("%b %d, %H:%M")(ts);
                } else {
                    return timeFormat("%b %d %Y, %H:%M")(ts);
                }
            }
        },

        mounted() {
            this.$refs.title.focus();

            this.unsubscribeActions = this.$store.subscribeAction(
                (action, state) => {
                    if (action.type == "requestLock") {
                        this.$store.commit("setBackup", {
                            editing: "note",
                            creds: this.noteEdit
                        });
                        this.$store.commit("encryptPack");
                    }
                }
            );
        },

        beforeDestroy() {
            this.unsubscribeActions();
        }
    };
</script>


<template>
    <div class="note-edit">
        <div class="modal-bg" @click="dismissEdit()"/>
        <div class="modal-container">
            <div class="dialog" v-if="showing == 'edit'">
                <input type="text" class="title" v-model="noteEdit.title"
                    :placeholder="typeLabel" autocomplete="off" ref="title">
                <textarea v-model="noteEdit.description" />

                <div class="controls">
                    <button @click="toggle('history')" class="history-button" v-show="(noteEdit.history || []).length > 1">
                        <Emoji name="timer" />
                    </button>
                    <button @click="deleteNote()">
                        Delete
                    </button>
                    <div class="spacer"></div>
                    <button @click="dismissEdit()">
                        Close
                    </button>
                    <button @click="saveChanges()">
                        Save
                    </button>
                </div>
            </div>


            <div class="dialog history" v-if="showing == 'history'">
                <div class="contents">
                    <h1>
                        Edit History
                    </h1>

                    <div class="dates">
                        <div class="date-row" v-for="[ts, historyRec] in note.history" :key="ts">
                            <div class="heading">
                                {{ formatTs(ts) }}
                            </div>

                            <div class="fields">
                                <template v-for="([field, val], index) in historyRec">
                                    <div :key="`field-${index}`" class="field">
                                        {{ field }}:
                                    </div>
                                    <div :key="`val-${index}`">
                                        {{ val }}
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="controls">
                    <button @click="toggle('edit')" class="history-button">
                        <Emoji name="pen" />
                    </button>
                    <div class="spacer" />
                    <button @click="dismissEdit()">
                        Close
                    </button>
                </div>
            </div>
        </div>
        </div>
    </div>
</template>


<style lang="scss">
    .note-edit {
        button {
            padding: 12px 25px;
            text-decoration: none;
            color: $input;

            &:hover,
            &:focus {
                text-decoration: none;
                outline: none;
            }
        }

        .modal-container .dialog {
            max-width: 40em;
            display: flex;
            height: 26em;
            max-height: 90vh;
            padding-bottom: 0;
        }

        .contents {
            flex-grow: 1;
        }

        .history {
            h1 {
                margin-top: 0;
                color: #888;
                font-size: 2em;
            }

            .heading {
                font-weight: 600;
                margin-bottom: 0.5em;
            }

            .dates {
                display: grid;
                gap: 1.5em;
                max-height: 24em;
                overflow-y: auto;
            }

            .fields {
                margin-left: 1em;
                display: grid;
                grid-template-columns: 1fr 10fr;

                .field {
                    text-align: right;
                    margin-right: 1em;
                    width: 6em;
                    text-transform: capitalize;
                }
            }
        }

        .history-button {
            flex-grow: 0;
            padding: 0;

            .emoji {
                max-width: 30px;
                max-height: 30px;
            }
        }

        .title {
            border: none;
            font-size: 2em;
            color: #333;
            width: 100%;
            margin-bottom: 1em;

            &:focus {
                outline: none;
            }
        }

        textarea {
            display: block;
            width: 100%;
            flex-grow: 1;
            padding: 15px;
            background: #f6f6f6;

            resize: none;
            border: none;

            font-family: sans serif;
            font-size: 1em;

            &:focus {
                outline: none;
            }
        }

        .controls {
            display: flex;
            padding: 1em 0;

            .spacer {
                flex-grow: 1;
            }

            & > * {
                margin-right: 0.5em;

                &:last-child {
                    margin-right: 0;
                }
            }
        }
    }
</style>


